import {
  GoogleAuthProvider,
  getRedirectResult,
  onAuthStateChanged,
  signInWithRedirect,
} from "firebase/auth";
import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import type { RedeemOutcome } from "@/lib/core/schemas/entitlements";
import { auth } from "@/lib/infrastructure/auth";
import logger from "@/lib/logger";

const TOKEN_STORAGE_KEY = "verderem.inviteToken";

export type RedeemPhase =
  | "checking" // resolving auth state
  | "signin" // unauthenticated — show the Google button
  | "redeeming" // POST in flight
  | "error" // server hiccup (5xx) or fetch/parse failure — NOT a dead link
  | RedeemOutcome;

const DEAD_LINK_PHASES: ReadonlySet<RedeemPhase> = new Set([
  "invalid",
  "expired",
  "revoked",
  "already_redeemed",
]);

/** Presentational — exported for RTL state tests (spec §12.5). */
export function RedeemOutcomeScreen({ phase }: { phase: RedeemPhase }) {
  const navigate = useNavigate();

  let body: React.ReactNode;
  if (phase === "signin") {
    body = (
      <>
        <p className="text-muted-foreground text-sm">
          Sign in with Google to redeem your invite.
        </p>
        <Button
          className="w-full"
          onClick={async () => {
            try {
              const provider = new GoogleAuthProvider();
              provider.setCustomParameters({ prompt: "select_account" });
              await signInWithRedirect(auth, provider);
            } catch (error) {
              logger.error({ err: error }, "[Invite] Sign-in redirect failed");
            }
          }}
          type="button"
        >
          Sign in with Google
        </Button>
      </>
    );
  } else if (phase === "redeeming") {
    body = (
      <p className="text-muted-foreground text-sm">Redeeming your invite...</p>
    );
  } else if (phase === "granted" || phase === "already_entitled") {
    body = (
      <p className="text-muted-foreground text-sm">
        Invite accepted — taking you to your campaigns.
      </p>
    );
  } else if (DEAD_LINK_PHASES.has(phase)) {
    body = (
      <>
        <p className="text-muted-foreground text-sm">
          This invite link is no longer valid. Ask Barry for a fresh one.
        </p>
        <Button
          className="w-full"
          onClick={() => navigate("/logout")}
          type="button"
          variant="outline"
        >
          Sign out
        </Button>
      </>
    );
  } else if (phase === "error") {
    body = (
      <>
        <p className="text-muted-foreground text-sm">
          Something went wrong — try the link again in a moment.
        </p>
        <Button
          className="w-full"
          onClick={() => navigate("/logout")}
          type="button"
          variant="outline"
        >
          Sign out
        </Button>
      </>
    );
  } else {
    body = <p className="text-muted-foreground text-sm">Loading...</p>;
  }

  return (
    <div className="flex h-dvh w-screen items-start justify-center bg-background pt-12 md:items-center md:pt-0">
      <div className="flex w-full max-w-md flex-col gap-8 rounded-2xl border border-zinc-200 p-8 text-center dark:border-zinc-800">
        <h3 className="font-semibold text-xl">Your invite</h3>
        {body}
      </div>
    </div>
  );
}

/**
 * Public invite-redemption route /invite/:token (spec §10.2). Stashes the
 * token in sessionStorage (survives the Google redirect round-trip), signs
 * the user in if needed, establishes the server session (/api/auth/login —
 * REQUIRED before redeem: it runs user_sync, and the entitlement row's FK
 * needs the User row), then redeems and routes on the outcome.
 */
export default function InvitePage() {
  const { token } = useParams<{ token: string }>();
  const navigate = useNavigate();
  const [phase, setPhase] = useState<RedeemPhase>("checking");
  const startedRedeem = useRef(false);

  const redeem = useCallback(async () => {
    if (startedRedeem.current) {
      return;
    }
    startedRedeem.current = true;
    setPhase("redeeming");
    const stashed = sessionStorage.getItem(TOKEN_STORAGE_KEY) ?? token ?? "";
    try {
      const response = await fetch("/api/invites/redeem", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ token: stashed }),
      });
      // A non-410 error response (400/500/502) is a server hiccup, not a
      // dead link — don't clear the stashed token so a reload can retry.
      if (!response.ok && response.status !== 410) {
        logger.error(
          { status: response.status },
          "[Invite] Redeem responded with an error"
        );
        setPhase("error");
        return;
      }
      const body = (await response.json()) as { outcome?: RedeemPhase };
      // A real outcome (2xx/410) was processed — the invite token has now
      // either been consumed or definitively rejected, so it's safe to clear.
      sessionStorage.removeItem(TOKEN_STORAGE_KEY);
      const outcome = body.outcome ?? "invalid";
      if (outcome === "granted" || outcome === "already_entitled") {
        navigate("/launch");
        return;
      }
      setPhase(outcome);
    } catch (error) {
      logger.error({ err: error }, "[Invite] Redeem failed");
      setPhase("error");
    }
  }, [navigate, token]);

  const establishSessionAndRedeem = useCallback(
    async (idToken: string) => {
      try {
        const response = await fetch("/api/auth/login", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ idToken }),
        });
        if (!response.ok) {
          throw new Error(await response.text());
        }
        await redeem();
      } catch (error) {
        logger.error({ err: error }, "[Invite] Failed to set server session");
        setPhase("signin");
      }
    },
    [redeem]
  );

  useEffect(() => {
    // Stash the token so it survives the Google redirect round-trip.
    if (token) {
      sessionStorage.setItem(TOKEN_STORAGE_KEY, token);
    }
  }, [token]);

  useEffect(() => {
    let mounted = true;
    let unsubscribe: (() => void) | undefined;

    const init = async () => {
      // AUTH_DISABLED dev stack: no Firebase — session is mocked server-side.
      if (window.__VERDEREM_CONFIG__?.authDisabled) {
        await redeem();
        return;
      }

      try {
        const result = await getRedirectResult(auth);
        if (!mounted) {
          return;
        }
        if (result?.user) {
          await establishSessionAndRedeem(await result.user.getIdToken());
          return;
        }
      } catch (error) {
        logger.error({ err: error }, "[Invite] Redirect error");
      }

      if (mounted) {
        unsubscribe = onAuthStateChanged(auth, async (user) => {
          if (!mounted) {
            return;
          }
          if (user) {
            await establishSessionAndRedeem(await user.getIdToken());
          } else {
            setPhase("signin");
          }
        });
      }
    };

    init();
    return () => {
      mounted = false;
      if (unsubscribe) {
        unsubscribe();
      }
    };
  }, [establishSessionAndRedeem, redeem]);

  return <RedeemOutcomeScreen phase={phase} />;
}
